import { useEffect, useRef, useState } from "react";
import { FaAngleDown, FaUser, FaKey, FaSignOutAlt } from "react-icons/fa";
import { HiMenuAlt2 } from "react-icons/hi";
import { useLocation, useNavigate } from "react-router-dom";
import { NavbarMobile } from "../navbar/NavbarMobile";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { logout } from "../../../redux/slices/adminSlice";
import { ChangePassword } from "../../admin/ChangePassword";
import { UpdateProfile } from "../../admin/UpdateProfile";

const ROUTE_LABELS: Record<string, string> = {
  "/": "Bảng điều khiển",
  "/admin": "Quản lý admin",
  "/category": "Danh mục",
  "/brand": "Thương hiệu",
  "/product": "Sản phẩm",
  "/order": "Đơn hàng",
  "/banner": "Banner",
  "/article": "Bài viết",
  "/page": "Trang",
  "/review": "Đánh giá",
  "/cache": "Cache",
};

export const Header = () => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { admin } = useAppSelector((state) => state.admin);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLabel = ROUTE_LABELS[location.pathname] ?? "";

  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate("/login", { replace: true });
  };

  const [showMenu, setShowMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showUpdateProfile, setShowUpdateProfile] = useState(false);

  return (
    <>
      <header className="bg-white border-b border-gray-100 px-5 h-14 flex items-center sticky top-0 z-30">
        <div className="flex items-center justify-between w-full gap-3">
          {/* Mobile menu + Section label */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => setShowMenu(true)}
              className="lg:hidden w-10 h-10 rounded-xl bg-gray-50 hover:bg-gray-100 flex items-center justify-center cursor-pointer transition-colors shrink-0"
            >
              <HiMenuAlt2 className="text-xl text-gray-600" />
            </button>
            {currentLabel && (
              <div className="flex items-center gap-2 text-sm text-gray-500 min-w-0">
                <span className="hidden sm:inline">Admin</span>
                <span className="hidden sm:inline text-gray-300">/</span>
                <span className="font-medium text-gray-800 truncate">
                  {currentLabel}
                </span>
              </div>
            )}
          </div>

          {/* User dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-3 pl-2 pr-3 py-1.5 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-linear-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0">
                <span className="text-xs font-bold text-white">
                  {admin?.name?.charAt(0)?.toUpperCase() ?? "A"}
                </span>
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-gray-800 leading-tight">
                  {admin?.name}
                </p>
                <p className="text-[11px] text-gray-400">
                  {admin?.role === "super-admin" ? "Super Admin" : "Admin"}
                </p>
              </div>
              <FaAngleDown
                className={`text-xs text-gray-400 transition-transform duration-200 ${
                  showUserMenu ? "rotate-180" : ""
                }`}
              />
            </button>

            <div
              className={`absolute top-full right-0 mt-1 w-56 bg-white border border-gray-100 rounded-xl p-1.5 shadow-xl shadow-gray-200/50 transition-all duration-200 z-50 ${
                showUserMenu
                  ? "opacity-100 pointer-events-auto translate-y-0"
                  : "opacity-0 pointer-events-none -translate-y-2"
              }`}
            >
              <div className="px-3 py-2 border-b border-gray-100 mb-1">
                <p className="text-sm font-medium text-gray-800 truncate">
                  {admin?.name}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {admin?.email}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowUpdateProfile(true);
                  setShowUserMenu(false);
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg cursor-pointer transition-colors"
              >
                <FaUser className="text-xs text-gray-400" />
                Hồ sơ cá nhân
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowChangePassword(true);
                  setShowUserMenu(false);
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg cursor-pointer transition-colors"
              >
                <FaKey className="text-xs text-gray-400" />
                Đổi mật khẩu
              </button>
              <div className="my-1 border-t border-gray-100" />
              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-500 hover:bg-red-50 rounded-lg cursor-pointer transition-colors"
              >
                <FaSignOutAlt className="text-xs" />
                Đăng xuất
              </button>
            </div>
          </div>
        </div>
      </header>

      {showMenu && (
        <div
          className="fixed inset-0 bg-black/40 z-998 lg:hidden"
          onClick={() => setShowMenu(false)}
        />
      )}
      <div
        className={`fixed inset-y-0 left-0 w-[80%] max-w-xs transition-transform z-999 duration-300 shadow-2xl lg:hidden ${
          showMenu ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <NavbarMobile handleCloseMenu={() => setShowMenu(false)} />
      </div>

      {showChangePassword && (
        <ChangePassword handleClose={() => setShowChangePassword(false)} />
      )}
      {showUpdateProfile && (
        <UpdateProfile handleClose={() => setShowUpdateProfile(false)} />
      )}
    </>
  );
};
